import { expandEmailRoutingWildcard } from '../Threads/utils/emailRoutingPattern'

export type ThreadFilterType = 'inbox' | 'conversations'

export type ThreadFilterable = {
  kind?: string
  routingParticipantId?: string | null
  communicationChannelId?: string | null
  campaignRecipient?: boolean
  campaignThreadId?: string | null
  isCampaignHub?: boolean
  /**
   * Set by the list query from an untruncated membership lookup.
   * When present, inbox classification uses this instead of the participant slice.
   */
  viewerIsParticipant?: boolean
  participants: Array<{
    kind: string
    memberId?: string | null
    displayName?: string
  }>
}

export function isCampaignRecipientThread(
  thread: Pick<ThreadFilterable, 'campaignRecipient' | 'campaignThreadId'>,
): boolean {
  return thread.campaignRecipient === true || !!thread.campaignThreadId
}

/** How long a campaign hub stays in the Conversations sidebar. */
export const CAMPAIGN_SIDEBAR_TTL_MS = 7 * 24 * 60 * 60 * 1000

/**
 * Sidebar label for one campaign hub. Campaigns have no name, so the label is
 * the send date and how many messages went out.
 */
export function formatCampaignGroupTitle(
  listedAtIso: string,
  messageCount: number | null,
  timeZone?: string,
): string {
  const listedAt = new Date(listedAtIso)
  if (Number.isNaN(listedAt.getTime())) {
    return messageCountLabel(messageCount) ?? 'Campaign'
  }

  const parts = new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone,
  }).formatToParts(listedAt)
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((entry) => entry.type === type)?.value ?? ''
  const dayPeriod = part('dayPeriod').replace(/\./g, '').toLowerCase()
  const when = `${part('day')} ${part('month')}, ${part('hour')}:${part('minute')} ${dayPeriod}`
  const countLabel = messageCountLabel(messageCount)
  return countLabel ? `${when} · ${countLabel}` : when
}

function messageCountLabel(messageCount: number | null): string | null {
  if (messageCount == null || messageCount < 0) return null
  return `${messageCount} ${messageCount === 1 ? 'message' : 'messages'}`
}

export function isRecentCampaignHub(
  thread: {
    isCampaignHub?: boolean
    campaignListedAt?: string | null
  },
  now: number = Date.now(),
): boolean {
  if (!thread.isCampaignHub || !thread.campaignListedAt) return false
  const listedAt = new Date(thread.campaignListedAt).getTime()
  if (Number.isNaN(listedAt)) return false
  return now - listedAt < CAMPAIGN_SIDEBAR_TTL_MS
}

export type ThreadSearchable = ThreadFilterable & {
  title?: string | null
  lastMessagePreview?: string | null
  routingPattern?: string | null
  routingPatternDisplay?: string | null
  routingAddress?: string | null
  isExclusiveFeed?: boolean
}

export function isFeedThread(
  thread: Pick<ThreadSearchable, 'isExclusiveFeed' | 'routingPattern'>,
): boolean {
  if (thread.isExclusiveFeed) return true
  return !!thread.routingPattern
}

export function getFeedRoutingLabel(
  thread: Pick<ThreadSearchable, 'routingPatternDisplay' | 'routingAddress'>,
): string | null {
  if (thread.routingPatternDisplay) {
    return normalizeWildcardPatternDisplay(thread.routingPatternDisplay)
  }
  return thread.routingAddress ?? null
}

function normalizeWildcardPatternDisplay(display: string): string {
  const trimmed = display.trim()
  const expanded = expandEmailRoutingWildcard(trimmed)
  if (expanded !== trimmed) return expanded

  if (!trimmed.startsWith('*')) return trimmed

  const rest = trimmed.slice(1)
  if (rest.startsWith('@')) return trimmed

  if (/^[a-z0-9][a-z0-9.-]*\.[a-z]{2,}$/i.test(rest)) {
    return `*@${rest}`
  }

  return trimmed
}

/** Client-side thread search (title, preview, participants, routing pattern). */
export function threadMatchesSearch(
  thread: ThreadSearchable,
  searchQuery: string,
): boolean {
  const query = searchQuery.trim().toLowerCase()
  if (!query) return true

  const haystacks: string[] = []
  if (thread.title) haystacks.push(thread.title)
  if (thread.lastMessagePreview) haystacks.push(thread.lastMessagePreview)
  if (thread.routingPattern) haystacks.push(thread.routingPattern)
  if (thread.routingPatternDisplay) {
    haystacks.push(thread.routingPatternDisplay)
  }
  if (thread.routingAddress) haystacks.push(thread.routingAddress)

  for (const participant of thread.participants) {
    if (participant.displayName) {
      haystacks.push(participant.displayName)
    }
  }

  return haystacks.some((value) => value.toLowerCase().includes(query))
}

export function isCurrentMemberThreadParticipant(
  thread: ThreadFilterable,
  currentMemberId: string | null,
): boolean {
  if (!currentMemberId) return false

  return thread.participants.some(
    (participant) =>
      participant.kind === 'MEMBER' && participant.memberId === currentMemberId,
  )
}

/** Inbox: threads where the current member is an active participant. */
export function isThreadInInbox(
  thread: ThreadFilterable,
  currentMemberId: string | null,
): boolean {
  if (thread.kind === 'TESTING' || thread.kind === 'CASE') return false
  if (thread.isCampaignHub) return false
  if (isCampaignRecipientThread(thread)) return false
  if (typeof thread.viewerIsParticipant === 'boolean') {
    return thread.viewerIsParticipant
  }
  return isCurrentMemberThreadParticipant(thread, currentMemberId)
}

/**
 * A room on a communication channel. It has no 1:1 routing participant.
 * Internal threads are member-to-app and have no communication channel.
 */
export function isChannelGroupThread(thread: {
  kind?: string
  communicationChannelId?: string | null
  routingParticipantId?: string | null
  isCampaignHub?: boolean
  campaignRecipient?: boolean
  campaignThreadId?: string | null
}): boolean {
  if (thread.kind === 'TESTING' || thread.kind === 'CASE') return false
  if (thread.isCampaignHub) return false
  if (thread.campaignRecipient || thread.campaignThreadId) return false
  return !!thread.communicationChannelId && !thread.routingParticipantId
}

/** Conversations: 1:1 external threads, and channel rooms once they are conversations. */
export function isThreadInConversations(thread: ThreadFilterable): boolean {
  if (thread.kind === 'TESTING' || thread.kind === 'CASE') return false
  if (thread.isCampaignHub) return false
  if (isCampaignRecipientThread(thread)) return false
  if (thread.routingParticipantId) return true
  return thread.kind === 'CONVERSATION' && isChannelGroupThread(thread)
}

/** Picks the default sidebar tab when navigating to a thread (not list filtering). */
export function classifyThreadFilter(
  thread: ThreadFilterable,
  currentMemberId: string | null,
): ThreadFilterType {
  if (isThreadInInbox(thread, currentMemberId)) return 'inbox'
  if (isThreadInConversations(thread)) return 'conversations'
  return 'inbox'
}
